
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Check, Video, Image, ArrowUpRight, Wand } from 'lucide-react';

interface ExampleGalleryProps {
  industry?: string;
}

const ExampleGallery: React.FC<ExampleGalleryProps> = ({ industry }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  const aiTools = [
    {
      id: 1,
      title: currentLanguage === 'de' ? "KI-Video Bearbeitung" : 
             currentLanguage === 'fr' ? "Édition Vidéo IA" :
             currentLanguage === 'it' ? "Editing Video IA" : "AI Video Editing",
      description: currentLanguage === 'de' ? "Unsere Experten transformieren Rohmaterial in professionelle Videos durch KI-basierte Automatisierung" : 
                  currentLanguage === 'fr' ? "Transformez des séquences brutes en vidéos professionnelles grâce à l'automatisation basée sur l'IA" :
                  currentLanguage === 'it' ? "Trasforma filmati grezzi in video professionali attraverso l'automazione basata sull'IA" : 
                  "Transform raw footage into professional videos with AI-based automation",
      icon: Video,
      benefit: currentLanguage === 'de' ? "60% schnellere Produktion" : 
              currentLanguage === 'fr' ? "Production 60% plus rapide" :
              currentLanguage === 'it' ? "Produzione più veloce del 60%" : 
              "60% faster production time"
    },
    {
      id: 2,
      title: currentLanguage === 'de' ? "KI-Bildgenerator" : 
             currentLanguage === 'fr' ? "Générateur d'Images IA" :
             currentLanguage === 'it' ? "Generatore di Immagini IA" : "AI Image Generator",
      description: currentLanguage === 'de' ? "Wir erstellen maßgeschneiderte Marketingbilder ohne Fotoshootings oder Stock-Fotos dank KI-Technologie" : 
                  currentLanguage === 'fr' ? "Créez des images marketing sur mesure sans séances photo ou photos de stock" :
                  currentLanguage === 'it' ? "Crea immagini di marketing personalizzate senza servizi fotografici o foto stock" : 
                  "Create custom marketing imagery without photoshoots or stock photos",
      icon: Image,
      benefit: currentLanguage === 'de' ? "Unbegrenzte kreative Möglichkeiten" : 
              currentLanguage === 'fr' ? "Possibilités créatives illimitées" :
              currentLanguage === 'it' ? "Possibilità creative illimitate" : 
              "Unlimited creative possibilities"
    },
    {
      id: 3,
      title: currentLanguage === 'de' ? "Video-Upscaling" : 
             currentLanguage === 'fr' ? "Upscaling Vidéo" :
             currentLanguage === 'it' ? "Upscaling Video" : "Video Upscaling",
      description: currentLanguage === 'de' ? "Die KI-Spezialisten verbessern niedrigauflösende Videos zu kristallklarer 4K-Qualität für maximale Wirkung" : 
                  currentLanguage === 'fr' ? "Améliorez des vidéos de basse résolution à une qualité 4K cristalline pour un impact maximal" :
                  currentLanguage === 'it' ? "Migliora i video a bassa risoluzione alla qualità cristallina 4K per il massimo impatto" : 
                  "Enhance low-resolution videos to crystal-clear 4K quality for maximum impact",
      icon: ArrowUpRight,
      benefit: currentLanguage === 'de' ? "Premium-Qualität für jede Plattform" : 
              currentLanguage === 'fr' ? "Qualité premium pour chaque plateforme" :
              currentLanguage === 'it' ? "Qualità premium per ogni piattaforma" : 
              "Premium quality for every platform"
    },
    {
      id: 4,
      title: currentLanguage === 'de' ? "Bild-Enhancement" : 
             currentLanguage === 'fr' ? "Amélioration d'Image" :
             currentLanguage === 'it' ? "Miglioramento Immagini" : "Image Enhancement",
      description: currentLanguage === 'de' ? "Future Level optimiert automatisch Farbe, Kontrast und Schärfe für perfekte visuelle Inhalte" : 
                  currentLanguage === 'fr' ? "Optimisation automatique de la couleur, du contraste et de la netteté pour un contenu visuel parfait" :
                  currentLanguage === 'it' ? "Ottimizzazione automatica di colore, contrasto e nitidezza per contenuti visivi perfetti" : 
                  "Automatic optimization of color, contrast, and sharpness for perfect visual content",
      icon: Wand,
      benefit: currentLanguage === 'de' ? "300% höhere Aufmerksamkeitsrate" : 
              currentLanguage === 'fr' ? "Taux d'attention supérieur de 300%" :
              currentLanguage === 'it' ? "Tasso di attenzione maggiore del 300%" : 
              "300% higher attention retention"
    }
  ];

  return (
    <section id="ai-tools-perks" className="py-16 bg-[#090911] relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-[#0A0A14] to-transparent"></div>
      
      {/* Subtle glow effects */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-900/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {currentLanguage === 'de' ? "Unser KI-gestützter Kreationsprozess" : 
             currentLanguage === 'fr' ? "Notre Processus de Création Assisté par IA" :
             currentLanguage === 'it' ? "Il Nostro Processo di Creazione Basato su IA" : 
             "Our AI-Powered Creation Process"}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-500 mx-auto mb-6"></div>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {currentLanguage === 'de' ? "Modernste KI-Technologien für herausragende Social Media Video- und Bildergebnisse" : 
             currentLanguage === 'fr' ? "Technologies d'IA de pointe pour des résultats vidéo et image exceptionnels sur les médias sociaux" :
             currentLanguage === 'it' ? "Tecnologie di IA all'avanguardia per risultati eccezionali di video e immagini per i social media" : 
             "Cutting-edge AI technologies for outstanding social media video and image results"}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {aiTools.map((tool, index) => (
            <div 
              key={tool.id}
              className="bg-[#0F0F18] border border-purple-900/30 rounded-lg p-5 transition-all hover:bg-[#12121E] hover:border-purple-500/40 group"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mr-3 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-colors">
                  <tool.icon className="w-5 h-5 text-purple-400 group-hover:text-purple-300" />
                </div>
                <span className="text-lg font-medium text-white">{index + 1}. {tool.title}</span>
              </div>
              <p className="text-gray-400 text-sm">{tool.description}</p>
              
              <div className="mt-3 flex items-center">
                <Check className="w-4 h-4 text-green-400 mr-1.5" />
                <span className="text-xs text-green-400">
                  {tool.benefit}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-10 text-center">
          <p className="text-purple-300 text-sm inline-block border-b border-purple-400 pb-1">
            {currentLanguage === 'de' ? "Fortlaufende Aktualisierung mit den neuesten KI-Algorithmen" : 
             currentLanguage === 'fr' ? "Mises à jour continues avec les algorithmes d'IA les plus récents" :
             currentLanguage === 'it' ? "Aggiornamento continuo con i più recenti algoritmi di IA" : 
             "Continuously updated with the latest AI algorithms"}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ExampleGallery;
